/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';

import { fg } from '@confluence/feature-gating';

import type {
	useVersionHistoryContentVersionHistoryQuery as useVersionHistoryContentVersionHistoryQuery$data,
	useVersionHistoryContentVersionHistoryQueryVariables as useVersionHistoryContentVersionHistoryQuery$variables,
} from './__types__/useVersionHistoryContentVersionHistoryQuery';

type useVersionHistoryType = {
	contentType: string;
	contentId: string;
};

export const useVersionHistory = ({ contentType, contentId }: useVersionHistoryType) => {
	const { data, loading, error, fetchMore, refetch } = useQuery<
		useVersionHistoryContentVersionHistoryQuery$data,
		useVersionHistoryContentVersionHistoryQuery$variables
	>(
		gql`
			query useVersionHistoryContentVersionHistoryQuery(
				$contentId: ID!
				$filter: ContentVersionHistoryFilter!
				$after: String
			) {
				contentVersionHistory(id: $contentId, after: $after, first: 30, filter: $filter) {
					nodes {
						author {
							id
							isActive
							displayName
						}
						contentId
						number
						updatedTime
						contentProperties {
							latest {
								externalVersionId
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
				}
			}
		`,
		{
			variables: { contentId, filter: { contentType } },
		},
	);
	const startCursor = data?.contentVersionHistory?.pageInfo?.startCursor || null;
	const hasNextPage = data?.contentVersionHistory?.pageInfo?.hasNextPage || false;

	// New BE behavior provides externalVersionId for all versions, and changes the first entry
	// from representing the "current" status of the board/db, to the most recently saved version.
	// We detect this by checking for externalVersionId on the first version, and if it exists we
	// return null for the "current" version to remove the special treatment of this version.
	// Note: We also gate this logic on a FG, to allow us to wait until translations are ready.
	// TODO: NCTD-3680 clean up logic that supports the current state of version history data
	const currentVersionHasExternalVersionId =
		Boolean(data?.contentVersionHistory?.nodes[0]?.contentProperties?.latest?.externalVersionId) &&
		fg('confluence_frontend_version_history_off_by_one_fix');
	const currentVersion = currentVersionHasExternalVersionId
		? null
		: data?.contentVersionHistory?.nodes[0] || null;
	const autosavedVersions = currentVersionHasExternalVersionId
		? data?.contentVersionHistory?.nodes || []
		: data?.contentVersionHistory?.nodes.slice(1) || [];

	const loadMoreVersions = () => {
		if (hasNextPage && startCursor && !loading) {
			void fetchMore({
				variables: { contentId, filter: { contentType }, after: startCursor },
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.contentVersionHistory) {
						return prev;
					}

					const prevNodes = prev?.contentVersionHistory?.nodes || [];
					const fetchedNodes = fetchMoreResult.contentVersionHistory.nodes || [];
					const pageInfo =
						fetchMoreResult.contentVersionHistory.pageInfo || prev?.contentVersionHistory?.pageInfo;

					return {
						contentVersionHistory: {
							nodes: [...prevNodes, ...fetchedNodes],
							pageInfo,
							__typename: 'ContentVersionHistoryConnection',
						},
					};
				},
			});
		}
	};

	return {
		currentVersion,
		autosavedVersions,
		loading,
		error,
		loadMoreVersions,
		cursor: startCursor,
		hasNextPage,
		refetch,
	};
};
